import TagManager from 'react-gtm-module';
import { normalizeAttribution } from './signup-tracking-data.js';

const STORAGE_KEY = 'hrfamly.signup-attribution.v1';
let memoryPending = null;
const pendingRequests = new Map();

export function rememberSignupAttribution(uid, attribution) {
  const values = normalizeAttribution(attribution);
  if (!Object.keys(values).length) return;
  memoryPending = { uid, attribution: values };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memoryPending));
  } catch {
    // Still save directly to Firebase when browser storage is unavailable.
  }
}

function readPending(uid) {
  let pending = memoryPending;
  if (!pending) {
    try {
      pending = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  }
  return pending?.uid === uid ? pending : null;
}

export async function persistSignupAttribution(user) {
  const pending = user && readPending(user.uid);
  if (!pending) return;
  if (pendingRequests.has(user.uid)) return pendingRequests.get(user.uid);
  const request = (async () => {
    const token = await user.getIdToken();
    const response = await fetch('/api/user/signup-attribution', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ attribution: pending.attribution }),
    });
    if (!response.ok) throw new Error('Signup attribution could not be saved');
    memoryPending = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // The server has already retained the attribution.
    }
  })();
  pendingRequests.set(user.uid, request);
  try {
    await request;
  } finally {
    pendingRequests.delete(user.uid);
  }
}

export async function dispatchTrialEvent(user, companyId) {
  const token = await user.getIdToken(true);
  const response = await fetch('/api/company/trial-event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ companyId }),
  });
  if (!response.ok) throw new Error('Trial event could not be claimed');
  const { event } = await response.json();
  if (!event) return;
  // Let GTM process the event before the full-page navigation; also work without GTM.
  await new Promise((resolve) => {
    const timer = setTimeout(resolve, 1800);
    TagManager.dataLayer({
      dataLayer: {
        ...event,
        eventTimeout: 1500,
        eventCallback: () => {
          clearTimeout(timer);
          resolve();
        },
      },
    });
  });
}
